import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 */
// END COPYRIGHT
// Adapted from neuro-san-ui packages/ui-common/components/Settings/ApiKeyInput.tsx
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useRef, useState } from "react";
import { isKeyValidationFailure } from "./providers";
const ApiKeyInput = ({ initialValue, onDirtyChange, onForget, onResultChange, onSave, onTest, placeholder, vendor, }) => {
    // Seeded from the store when the dialog opens, then kept in step by this row's own saves.
    const [savedValue, setSavedValue] = useState(initialValue);
    const [inputValue, setInputValue] = useState(initialValue);
    const [keyValidated, setKeyValidated] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [showKey, setShowKey] = useState(false);
    // The key a running test is for. A result that lands after the field has moved on
    // describes a key the user is no longer looking at, so it is dropped.
    const testedValue = useRef(null);
    const clearVendorResult = () => {
        testedValue.current = null;
        setKeyValidated(null);
        onResultChange?.(vendor, null);
    };
    // Every edit reports whether the field now differs from the saved key.
    const setEditedValue = (value) => {
        setInputValue(value);
        onDirtyChange?.(vendor, value !== savedValue);
    };
    // Called once the store holds this value, so the field matches it and nothing is unsaved.
    const setStoredValue = (value) => {
        setInputValue(value);
        setSavedValue(value);
        onDirtyChange?.(vendor, false);
    };
    const handleValueChange = (e) => {
        clearVendorResult();
        setEditedValue(e.target.value);
    };
    const handleOnSave = () => {
        // The key is stored trimmed, so the field drops the whitespace it was pasted with
        const keyToSave = inputValue.trim();
        onSave(keyToSave);
        setStoredValue(keyToSave);
    };
    const handleOnForget = () => {
        setConfirmationDialogOpen(false);
        onForget();
        clearVendorResult();
        setStoredValue("");
    };
    const handleOnTest = async () => {
        // A pasted key often arrives wrapped in whitespace, which the server rejects outright
        const keyUnderTest = inputValue.trim();
        testedValue.current = keyUnderTest;
        setIsValidating(true);
        setKeyValidated(null);
        try {
            const result = await onTest(keyUnderTest);
            if (testedValue.current !== keyUnderTest)
                return;
            setKeyValidated(result.ok);
            onResultChange?.(vendor, isKeyValidationFailure(result) ? result : null);
        }
        finally {
            setIsValidating(false);
        }
    };
    const disableActions = !inputValue || inputValue === savedValue || isValidating;
    const labelId = `byok-${vendor.toLowerCase()}-label`;
    const statusColor = keyValidated === null ? "action.disabled" : keyValidated ? "success.main" : "error.main";
    const statusLabel = keyValidated === null ? "not tested" : keyValidated ? "key works" : "key rejected";
    return (_jsxs(Box, { "aria-label": `${vendor} API key`, role: "group", sx: { display: "flex", alignItems: "center", width: "100%", gap: 2 }, children: [_jsxs(Dialog, { open: confirmationDialogOpen, onClose: () => setConfirmationDialogOpen(false), children: [_jsxs(DialogTitle, { children: ["Forget ", vendor, " API key?"] }), _jsx(DialogContent, { children: _jsxs(DialogContentText, { children: ["This will forget the currently saved API key for ", vendor, " and you will need to enter the key again to use networks that require it. Are you sure you want to continue?"] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { color: "inherit", onClick: () => setConfirmationDialogOpen(false), children: "Cancel" }), _jsx(Button, { onClick: handleOnForget, children: "Yes, forget key" })] })] }), _jsx(FormLabel, { id: labelId, sx: { width: 90, flexShrink: 0 }, children: vendor }), _jsx(TextField, { autoComplete: "off", onChange: handleValueChange, placeholder: placeholder, size: "small", slotProps: {
                    htmlInput: { "aria-labelledby": labelId },
                    input: {
                        endAdornment: (_jsxs(InputAdornment, { position: "end", children: [_jsx(Tooltip, { title: showKey ? "Hide API key" : "Show API key", children: _jsx("span", { children: _jsx(IconButton, { "aria-label": showKey ? "Hide API key" : "Show API key", disabled: !inputValue, onClick: () => setShowKey(!showKey), onMouseDown: (e) => e.preventDefault(), size: "small", sx: {
                                                color: "primary.main",
                                                "&.Mui-disabled": { color: "primary.main" },
                                            }, children: showKey ? (_jsx(VisibilityOffIcon, { fontSize: "small" })) : (_jsx(VisibilityIcon, { fontSize: "small" })) }) }) }), _jsx(IconButton, { "aria-label": "Clear input", color: "primary", edge: "end", onClick: () => {
                                        clearVendorResult();
                                        setEditedValue("");
                                    }, size: "small", children: _jsx(ClearIcon, { fontSize: "small" }) })] })),
                    },
                }, sx: { flex: 1 }, type: showKey ? "text" : "password", value: inputValue, variant: "outlined" }), _jsx(Box, { "aria-label": `${vendor} ${statusLabel}`, role: "status", sx: {
                    backgroundColor: statusColor,
                    borderRadius: "50%",
                    display: "inline-block",
                    minHeight: "0.75rem",
                    minWidth: "0.75rem",
                } }), _jsx(Button, { disabled: disableActions, loading: isValidating, onClick: () => {
                    void handleOnTest();
                }, size: "small", variant: "contained", children: "Test" }), _jsx(Button, { disabled: disableActions, onClick: handleOnSave, size: "small", variant: "contained", children: "Save" }), _jsx(Button, { disabled: !savedValue || isValidating, onClick: () => setConfirmationDialogOpen(true), size: "small", variant: "contained", children: "Forget" })] }));
};
export default ApiKeyInput;
